/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect, useRef } from 'react';
import printerImg from 'dndimages/printer-icon.png';
import './PrintViewButton.css';

export type PrintMode = 'append' | 'new';

interface PrintModeOption {
  id: PrintMode;
  name: string;
}

// Split button (like 'Export to FG'): the main half prints, the ▼ half picks where the print goes.
// The label always reads 'Print View' regardless of the selected mode.
const printModeOptions: PrintModeOption[] = [
  { id: 'new',    name: 'New tab' },
  { id: 'append', name: 'Append to existing' },
];

interface PrintViewButtonProps {
  onPrint: (mode: PrintMode) => void;
}

const PrintViewButton: React.FC<PrintViewButtonProps> = ({ onPrint }) => {
  const [printMode, setPrintMode] = useState<PrintMode>('append');  // append is the default
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load saved preference
  useEffect(() => {
    const saved = localStorage.getItem('preferredPrintMode');
    if (saved && printModeOptions.some(opt => opt.id === saved)) {
      setPrintMode(saved as PrintMode);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handlePrintModeSelect = (modeId: PrintMode) => {
    setPrintMode(modeId);
    localStorage.setItem('preferredPrintMode', modeId);
    setIsDropdownOpen(false);
  };

  return (
    <div className="print-view-button-container" id="printViewButton" ref={dropdownRef}>
      <button onClick={() => onPrint(printMode)} className="print-view-button-main"
              title="open new tab with a black & white view of the monster, suitable for printing">
        <img src={printerImg} alt="Print View" width="16px" />
        Print View
      </button>
      <button onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="print-view-button-dropdown" title="Choose where the print goes">▼</button>

      {isDropdownOpen && (
        <div className="print-view-dropdown-menu">
          {printModeOptions.map(option => (
            <div
              key={option.id}
              className={`print-view-dropdown-item ${option.id === printMode ? 'selected' : ''}`}
              onClick={() => handlePrintModeSelect(option.id)}
            >
              <span className="print-view-name">{option.name}</span>
              {option.id === printMode && <span className="print-view-checkmark">✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PrintViewButton;
